import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import {
  Controller,
  useFormContext,
  type FieldError,
  type UseFormRegisterReturn,
} from "react-hook-form";
import { stateOptions } from "../../../../constants";
import { useAddAddress } from "../../../../hooks/useAddAddress";
import type { ShippingLabelFormValues } from "../../../../types/forms";

interface NewAddressFormProps {
  onCancel: () => void;
}

const trim = (v?: string) => v?.trim();

export default function NewAddressForm({ onCancel }: NewAddressFormProps) {
  const {
    register,
    control,
    trigger,
    getValues,
    setValue,
    unregister,
    formState: { errors },
  } = useFormContext<ShippingLabelFormValues>();

  const {
    mutate: saveAddress,
    isPending,
    isError,
  } = useAddAddress({
    onSuccess: (savedAddress) => {
      setValue("fromAddressId", savedAddress.id, { shouldValidate: true });
      unregister("newAddress");
      onCancel();
    },
  });

  const handleSave = async () => {
    if (await trigger("newAddress")) {
      saveAddress(getValues("newAddress"));
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 md:p-5 p-4">
      <div className="flex flex-col gap-5">
        <TextField
          id="newAddressLabel"
          label="Address Label"
          placeholder="e.g. Home"
          registration={register("newAddress.label", {
            required: "Address label is required",
            maxLength: {
              value: 50,
              message: "Address label must be at most 50 characters",
            },
            setValueAs: trim,
          })}
          error={errors.newAddress?.label}
        />
        <TextField
          id="newAddressRecipientName"
          label="Full Name"
          placeholder="Enter your full name"
          registration={register("newAddress.recipientName", {
            required: "Full name is required",
            maxLength: {
              value: 100,
              message: "Full name must be at most 100 characters",
            },
            setValueAs: trim,
          })}
          error={errors.newAddress?.recipientName}
        />
        <TextField
          id="newAddressStreet"
          label="Address"
          placeholder="Enter your address"
          registration={register("newAddress.address.street", {
            required: "Address is required",
            maxLength: {
              value: 100,
              message: "Street must be at most 100 characters",
            },
            setValueAs: trim,
          })}
          error={errors.newAddress?.address?.street}
        />
        <TextField
          id="newAddressUnit"
          label="Apt, Suite, Building (optional)"
          placeholder="Enter Apt, Suite, Building"
          registration={register("newAddress.address.unit", {
            maxLength: {
              value: 50,
              message: "Unit must be at most 50 characters",
            },
            setValueAs: trim,
          })}
          error={errors.newAddress?.address?.unit}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <TextField
            id="newAddressCity"
            label="City"
            placeholder="Enter your City"
            registration={register("newAddress.address.city", {
              required: "City is required",
              maxLength: {
                value: 50,
                message: "City must be at most 50 characters",
              },
              setValueAs: trim,
            })}
            error={errors.newAddress?.address?.city}
          />

          <Controller
            name="newAddress.address.state"
            control={control}
            rules={{ required: "State is required" }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-2">
                <label className="block text-sm/6 font-normal text-gray-950">
                  State
                </label>
                <Listbox
                  value={
                    stateOptions.find((o) => o.value === field.value) ?? null
                  }
                  onChange={(opt) => field.onChange(opt?.value)}
                >
                  <ListboxButton
                    className={clsx(
                      "flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-sm outline-none",
                      "focus:ring-2 focus:ring-blue-500",
                      fieldState.error
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-200",
                    )}
                  >
                    <span className="truncate">
                      {stateOptions.find((o) => o.value === field.value)
                        ?.label ?? (
                        <span className="text-gray-500">Select state</span>
                      )}
                    </span>
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  </ListboxButton>
                  <ListboxOptions
                    anchor="bottom"
                    className="mt-1 w-(--button-width) h-46 overflow-auto rounded-xl border border-gray-200 bg-white p-1 shadow outline-none"
                  >
                    {stateOptions.map((opt) => (
                      <ListboxOption
                        key={opt.value}
                        value={opt}
                        className="cursor-pointer rounded-lg px-3 py-2 text-sm data-focus:bg-blue-50 data-focus:text-blue-500"
                      >
                        {opt.label}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Listbox>
                {fieldState.error && (
                  <p className="text-sm text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <TextField
            id="newAddressZip"
            label="ZIP Code"
            placeholder="Enter your ZIP Code"
            registration={register("newAddress.address.zip", {
              required: "ZIP Code is required",
              pattern: {
                value: /^\d{5}(-\d{4})?$/,
                message: "ZIP must be a valid US postal code",
              },
              setValueAs: trim,
            })}
            error={errors.newAddress?.address?.zip}
          />
        </div>
      </div>

      {isError && (
        <p className="mt-5 text-sm text-red-500">
          Failed to save address. Please try again.
        </p>
      )}

      <div className="mt-5 flex md:gap-4 gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={isPending}
          className="h-13 w-full rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isPending}
          className="h-13 w-full rounded-xl text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

const TextField = ({
  id,
  label,
  placeholder,
  registration,
  error,
}: {
  id: string;
  label: string;
  placeholder: string;
  registration: UseFormRegisterReturn;
  error?: FieldError;
}) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="block text-sm/6 font-normal text-gray-950">
      {label}
    </label>
    <input
      id={id}
      type="text"
      autoComplete="off"
      placeholder={placeholder}
      {...registration}
      className={clsx(
        "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
        "focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500",
        error ? "border-red-500 focus:ring-red-500" : "border-gray-200",
      )}
    />
    {error && <p className="text-sm text-red-500">{error.message}</p>}
  </div>
);
